import React, { useEffect, useId, useRef, useState } from "react";

import { BasicButton, BasicIconButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Close, Delete, Upload } from "@gaming/xds-react-js-icons";

import { Modal, ModalInitialFocus } from "../../../../components/Modal/Modal";
import { SelectDropdown } from "../../../../components/SelectDropdown/SelectDropdown";
import { isValidDunsNumber, normalizeDunsNumber } from "../../../../constants/dunsNumber";
import { useStrings } from "../../../../localization/localizationHelpers";
import type { XboxOnboardEnrollmentEvidence } from "../../../../server/crm/api/models/xboxOnboardModels";
import * as styles from "./EmbargoedEnrollmentModal.module.css";

const maxFileSize = 5 * 1024 * 1024;
type EvidenceType = "duns" | "document";

type EmbargoedEnrollmentModalProps = Readonly<{
  isOpen: boolean;
  initialDunsValue?: string;
  onClose: () => void;
  onSubmit: (evidence: XboxOnboardEnrollmentEvidence) => void;
}>;

export const EmbargoedEnrollmentModal = ({
  isOpen,
  initialDunsValue,
  onClose,
  onSubmit,
}: EmbargoedEnrollmentModalProps) => {
  const titleId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [evidenceType, setEvidenceType] = useState<EvidenceType>();
  const [dunsValue, setDunsValue] = useState(initialDunsValue ?? "");
  const [file, setFile] = useState<File>();
  const [documentType, setDocumentType] = useState("BV_001");
  const [fileError, setFileError] = useState<string>();
  const [
    title,
    description,
    dunsDescription,
    dunsLabel,
    dunsPlaceholder,
    invalidDunsNumber,
    dunsOptionLabel,
    fileUploadTitle,
    fileUploadSubtitle,
    documentTypeLabel,
    uploadDocumentLabel,
    browseFileLabel,
    fileDetail,
    tooBigFileError,
    wrongTypeFileError,
    deleteFileLabel,
    cancelLabel,
    startEnrollmentLabel,
    closeLabel,
    documentTypeOne,
    documentTypeTwo,
    documentTypeThree,
    documentTypeFour,
    documentTypeFive,
    documentTypeSix,
  ] = useStrings(
    "idXboxGdkEmbargoTitle",
    "idXboxGdkEmbargoEvidenceDescription",
    "idXboxGdkEmbargoDunsDescription",
    "welcomePartnerPageDunsNumberTitle",
    "xboxOnboardDunsNumberPlaceholder",
    "invalidDunsNumber",
    "dunsNumberSectionTitle",
    "welcomePartnerFileUploadTitle",
    "welcomePartnerFileUploadSubtitle",
    "welcomePartnerFileUploadTypeDocumentTitle",
    "welcomePartnerFileUploadContainerTitle",
    "welcomePartnerFileUploadBrowseFile",
    "welcomePartnerFileUploadFileDetail",
    "welcomePartnerFileUploadTooBigFileRejectedError",
    "welcomePartnerFileUploadWrongTypeRejectedError",
    "welcomePartnerPageFileUploadDeleteFile",
    "cancelButton",
    "idXboxGdkEmbargoStartEnrollment",
    "closeAriaLabel",
    "welcomePartnerPageFileUploadTypeOne",
    "welcomePartnerPageFileUploadTypeTwo",
    "welcomePartnerPageFileUploadTypeThree",
    "welcomePartnerPageFileUploadTypeFour",
    "welcomePartnerPageFileUploadTypeFive",
    "welcomePartnerPageFileUploadTypeSix"
  );

  useEffect(() => {
    if (isOpen) {
      setEvidenceType(initialDunsValue?.trim() ? "duns" : undefined);
      setDunsValue(initialDunsValue ?? "");
      setFile(undefined);
      setDocumentType("BV_001");
      setFileError(undefined);
    }
  }, [initialDunsValue, isOpen]);

  const normalizedDunsValue = normalizeDunsNumber(dunsValue);
  const hasValidDunsValue = Boolean(dunsValue.trim() && isValidDunsNumber(normalizedDunsValue));
  const dunsError =
    evidenceType === "duns" && dunsValue.trim() && !hasValidDunsValue
      ? invalidDunsNumber
      : undefined;
  const canSubmit =
    evidenceType === "duns" ? hasValidDunsValue : evidenceType === "document" && Boolean(file);

  const selectEvidenceType = (nextEvidenceType: EvidenceType) => {
    setEvidenceType(nextEvidenceType);
    setFileError(undefined);
    if (nextEvidenceType === "duns") {
      removeFile();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    const isPdf =
      selectedFile.type === "application/pdf" || selectedFile.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setFile(undefined);
      setFileError(wrongTypeFileError);
      event.target.value = "";
      return;
    }
    if (selectedFile.size > maxFileSize) {
      setFile(undefined);
      setFileError(tooBigFileError);
      event.target.value = "";
      return;
    }

    setFile(selectedFile);
    setFileError(undefined);
  };

  const removeFile = () => {
    setFile(undefined);
    setFileError(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    onSubmit({
      dunsValue: evidenceType === "duns" ? normalizedDunsValue : undefined,
      fileName: evidenceType === "document" ? file?.name : undefined,
      chunk: evidenceType === "document" ? file : undefined,
      typeFile: evidenceType === "document" ? documentType : undefined,
    });
  };

  const documentTypeOptions = [
    { id: "BV_001", label: documentTypeOne },
    { id: "BV_002", label: documentTypeTwo },
    { id: "BV_004", label: documentTypeThree },
    { id: "BV_005", label: documentTypeFour },
    { id: "BV_006", label: documentTypeFive },
    { id: "BV_007", label: documentTypeSix },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy={titleId}
      initialFocus={ModalInitialFocus.FirstInput}
      closeOnOutsideClick={false}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            aria-label={closeLabel}
            onClick={onClose}
          >
            <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.description}>{description}</p>

          <fieldset className={styles.evidenceOptions}>
            <legend className={styles.visuallyHidden}>{title}</legend>
            <label className={styles.evidenceOption}>
              <input
                type="radio"
                name={`${titleId}-evidence-type`}
                checked={evidenceType === "duns"}
                onChange={() => selectEvidenceType("duns")}
              />
              <span>{dunsOptionLabel}</span>
            </label>
            <label className={styles.evidenceOption}>
              <input
                type="radio"
                name={`${titleId}-evidence-type`}
                checked={evidenceType === "document"}
                onChange={() => selectEvidenceType("document")}
              />
              <span>{fileUploadTitle}</span>
            </label>
          </fieldset>

          {evidenceType === "duns" && (
            <section className={styles.section}>
              <label className={styles.sectionTitle} htmlFor={`${titleId}-duns`}>
                {dunsLabel}
              </label>
              <p className={styles.hint}>{dunsDescription}</p>
              <input
                id={`${titleId}-duns`}
                className={styles.input}
                value={dunsValue}
                onChange={event => setDunsValue(event.target.value)}
                placeholder={dunsPlaceholder}
                inputMode="numeric"
                maxLength={11}
                aria-invalid={Boolean(dunsError)}
                aria-describedby={dunsError ? `${titleId}-duns-error` : undefined}
              />
              {dunsError && (
                <p id={`${titleId}-duns-error`} className={styles.error} role="alert">
                  {dunsError}
                </p>
              )}
            </section>
          )}

          {evidenceType === "document" && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{fileUploadTitle}</h3>
              <p className={styles.hint}>{fileUploadSubtitle}</p>
              <label className={styles.label}>{documentTypeLabel}</label>
              <SelectDropdown
                options={documentTypeOptions}
                value={documentType}
                onChange={setDocumentType}
                aria-label={documentTypeLabel}
                className={styles.documentTypeDropdown}
              />
              <span className={styles.label}>{uploadDocumentLabel}</span>
              <div className={styles.uploadBox}>
                <Icon aria-hidden="true" icon={Upload} iconSize={IconSize.Custom} />
                <input
                  ref={fileInputRef}
                  className={styles.fileInput}
                  type="file"
                  accept=".pdf,application/pdf"
                  aria-label={browseFileLabel}
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className={styles.browseButton}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {browseFileLabel}
                </button>
                <span className={styles.fileDetail}>{fileDetail}</span>
              </div>
              {fileError && (
                <p className={styles.error} role="alert">
                  {fileError}
                </p>
              )}
              {file && (
                <div className={styles.fileRow}>
                  <span className={styles.fileName}>{file.name}</span>
                  <BasicIconButton
                    as="button"
                    buttonType={ButtonType.Secondary}
                    icon={Delete}
                    aria-label={deleteFileLabel}
                    onClick={removeFile}
                  />
                </div>
              )}
            </section>
          )}
        </div>

        <div className={styles.actions}>
          <BasicButton
            as="button"
            type="button"
            buttonType={ButtonType.Secondary}
            onClick={onClose}
          >
            {cancelLabel}
          </BasicButton>
          <BasicButton
            as="button"
            type="button"
            buttonType={ButtonType.Brand}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {startEnrollmentLabel}
          </BasicButton>
        </div>
      </div>
    </Modal>
  );
};
